// src/modules/accounting/pages/GeneralLedgerPage.jsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Spin, Alert, Typography, Row, Col, DatePicker, Button, Space,
  Statistic, Select, Empty, Tooltip, Divider, Tag, Switch
} from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAccounts, getAccountLedger, getAllAccountBalances } from '../api';
import { ACCOUNT_NATURES, NATURE_MAP } from '../constants';
import DataTable from '../../../components/UI/DataTable';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function GeneralLedgerPage() {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  // Parent accounts in the Chart of Accounts hold no postings of their own —
  // their balance is their children's. Rolled up by default; turn off to see
  // only what was posted directly to the selected account.
  const [includeChildren, setIncludeChildren] = useState(true);

  // Load accounts list
  useEffect(() => {
    (async () => {
      try {
        const res = await getAccounts({ limit: 200 });
        // Handle response format: { success: true, data: [...accounts], total: ... }
        const accountsData = res.data || res.accounts || res || [];
        setAccounts(Array.isArray(accountsData) ? accountsData : []);
      } catch (err) {
        console.error('Failed to load accounts', err);
        setAccounts([]); // Ensure accounts is always an array on error
      }
    })();
  }, []);

  // Load all balances for overview
  useEffect(() => {
    (async () => {
      setBalanceLoading(true);
      try {
        const res = await getAllAccountBalances();
        // Response envelope: { success, data: { balances: [...], summary: {...} } }
        const rawBalances = res.data?.balances || res.balances || res.data || res || [];
        const balancesArr = Array.isArray(rawBalances) ? rawBalances : [];
        // Flatten nested account object so table dataIndex works directly
        setBalances(balancesArr.map(b => ({
          ...b.account,
          balance: b.balance,
          totalDebit: b.totalDebit,
          totalCredit: b.totalCredit,
          transactionCount: b.transactionCount,
          ownBalance: b.ownBalance,
          hasChildren: b.hasChildren,
          descendantCount: b.descendantCount,
          isChild: b.isRolledUpElsewhere,
        })));
      } catch (err) {
        console.error('Failed to load balances', err);
        setBalances([]); // Ensure balances is always an array on error
      } finally {
        setBalanceLoading(false);
      }
    })();
  }, []);

  const fetchLedger = useCallback(async () => {
    if (!selectedAccountId) return;
    setLoading(true);
    setError(null);
    try {
      const params = { includeChildren: includeChildren ? 'true' : 'false' };
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].startOf('day').toISOString();
        params.endDate = dateRange[1].endOf('day').toISOString();
      }
      const res = await getAccountLedger(selectedAccountId, params);
      setLedgerData(res.data || res);
    } catch (err) {
      setError(err.message || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId, dateRange, includeChildren]);

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');
  const typeColors = { ASSET: 'blue', LIABILITY: 'red', EQUITY: 'purple', REVENUE: 'green', EXPENSE: 'orange' };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={5} style={{ margin: 0 }}>General Ledger</Title></Col>
        <Col>
          <Space>
            <Select
              showSearch
              style={{ width: 320 }}
              placeholder="Select account..."
              optionFilterProp="label"
              value={selectedAccountId}
              onChange={setSelectedAccountId}
              options={Array.isArray(accounts) ? accounts.map((a) => ({
                value: a._id,
                label: `${a.code} — ${a.name} (${a.type})`,
              })) : []}
              allowClear
            />
            <RangePicker value={dateRange} onChange={setDateRange} format="DD/MM/YYYY" allowClear />
            <Tooltip title="A parent account's balance is the sum of its child accounts. Turn this off to see only what was posted directly to the selected account.">
              <Space size={4}>
                <Switch size="small" checked={includeChildren} onChange={setIncludeChildren} />
                <Text style={{ fontSize: 12 }}>Include child accounts</Text>
              </Space>
            </Tooltip>
            <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={fetchLedger} /></Tooltip>
          </Space>
        </Col>
      </Row>

      {/* If no account selected — show all balances overview */}
      {!selectedAccountId && (
        <>
          <Title level={5}>All Account Balances</Title>
          {balanceLoading ? <Spin /> : (
            <DataTable persistId="accounting.general-ledger.balances"
              dataSource={Array.isArray(balances) ? balances.filter(b => b.balance !== 0).map((b, i) => ({ ...b, key: i })) : []}
              columns={[
                { title: 'Code', dataIndex: 'code', width: 80, sorter: (a, b) => a.code.localeCompare(b.code) },
                {
                  title: 'Account',
                  dataIndex: 'name',
                  render: (name, rec) => (
                    <Space size={4}>
                      {rec.isChild && <Text type="secondary" style={{ fontFamily: 'monospace' }}>↳</Text>}
                      <span>{name}</span>
                      {rec.hasChildren && (
                        <Tooltip title={`Group account — this balance is the total of ${rec.descendantCount} child account(s), each also listed below.`}>
                          <Tag color="geekblue" style={{ marginInlineStart: 4 }}>Group</Tag>
                        </Tooltip>
                      )}
                    </Space>
                  ),
                },
                { title: 'Nature', dataIndex: 'nature', width: 190,
                  render: (n, rec) => NATURE_MAP[n]
                    ? <Tag color={typeColors[NATURE_MAP[n].type]}>{NATURE_MAP[n].label}</Tag>
                    : <Tag color={typeColors[rec.type]}>{rec.type || 'Unclassified'}</Tag>,
                  filters: [
                    ...ACCOUNT_NATURES.map(n => ({ text: n.label, value: n.value })),
                    { text: 'Unclassified', value: 'UNCLASSIFIED' },
                  ],
                  onFilter: (val, rec) => (val === 'UNCLASSIFIED' ? !rec.nature : rec.nature === val),
                },
                {
                  title: 'Own', dataIndex: 'ownBalance', align: 'right', width: 130,
                  render: (v, rec) => rec.hasChildren
                    ? <Text type="secondary">₹{fmt(v)}</Text>
                    : <Text type="secondary">—</Text>,
                },
                { title: 'Balance', dataIndex: 'balance', render: (v) => `₹${fmt(v)}`, align: 'right', width: 160,
                  sorter: (a, b) => a.balance - b.balance,
                },
              ]}
              pagination={{ pageSize: 25 }}
              size="small"
              onRow={(record) => ({
                onClick: () => setSelectedAccountId(record._id),
                style: { cursor: 'pointer' },
              })}
            />
          )}
        </>
      )}

      {/* Ledger Detail */}
      {selectedAccountId && (
        <>
          {loading && <Spin size="large" style={{ display: 'block', margin: '40px auto' }} />}
          {error && <Alert type="error" message={error} showIcon />}
          {ledgerData && !loading && (
            <>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col flex="auto">
                    <Title level={5} style={{ margin: 0 }}>
                      {ledgerData.account?.code} — {ledgerData.account?.name}
                      <Tag color={typeColors[ledgerData.account?.type]} style={{ marginLeft: 8 }}>
                        {NATURE_MAP[ledgerData.account?.nature]?.label || ledgerData.account?.type}
                      </Tag>
                    </Title>
                  </Col>
                  {ledgerData.hierarchy?.includesChildren && (
                    <Col><Statistic title="Own postings" value={fmt(ledgerData.hierarchy.ownBalance)} prefix="₹" valueStyle={{ fontSize: 18 }} /></Col>
                  )}
                  <Col><Statistic title="Opening" value={fmt(ledgerData.openingBalance)} prefix="₹" /></Col>
                  <Col>
                    <Statistic
                      title={ledgerData.hierarchy?.includesChildren ? 'Closing (incl. children)' : 'Closing'}
                      value={fmt(ledgerData.closingBalance)} prefix="₹" valueStyle={{ fontWeight: 'bold' }}
                    />
                  </Col>
                </Row>

                {/* Where a group account's balance actually comes from. */}
                {ledgerData.hierarchy?.children?.length > 0 && (
                  <>
                    <Divider style={{ margin: '12px 0 8px' }} />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Group account — balance rolled up from {ledgerData.hierarchy.descendantCount} child account(s):
                    </Text>
                    <div style={{ marginTop: 6 }}>
                      <Space size={[8, 8]} wrap>
                        {ledgerData.hierarchy.children.map((c) => (
                          <Tag
                            key={c._id}
                            color={c.balance ? 'blue' : undefined}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedAccountId(c._id)}
                          >
                            {c.code} {c.name} · ₹{fmt(c.balance)}
                            {c.descendantCount > 0 && ` (+${c.descendantCount})`}
                            {c.isActive === false && ' · disabled'}
                          </Tag>
                        ))}
                      </Space>
                    </div>
                  </>
                )}

                {ledgerData.hierarchy?.hasChildren && !includeChildren && (
                  <Alert
                    style={{ marginTop: 12 }} type="warning" showIcon
                    message="Showing this account's own postings only"
                    description="This is a parent account. Turn on “Include child accounts” to see the balance its children carry."
                  />
                )}
              </Card>

              <DataTable persistId="accounting.general-ledger.entries"
                dataSource={(ledgerData.entries || []).map((t, i) => ({ ...t, key: i }))}
                columns={[
                  { title: 'Txn Date', dataIndex: 'date', width: 100, render: (d) => d ? dayjs(d).format('DD/MM/YYYY') : '—' },
                  {
                    // Only interesting when it differs from the transaction date — an
                    // entry booked after the fact. Same-day postings stay muted.
                    title: 'Entry Date', dataIndex: 'entryDate', width: 100,
                    render: (d, r) => {
                      if (!d) return <Text type="secondary">—</Text>;
                      const deferred = r.date && !dayjs(d).isSame(dayjs(r.date), 'day');
                      return deferred
                        ? <Tag color="gold" style={{ marginInlineEnd: 0 }}>{dayjs(d).format('DD/MM/YYYY')}</Tag>
                        : <Text type="secondary">{dayjs(d).format('DD/MM/YYYY')}</Text>;
                    },
                  },
                  { title: 'Entry ID', dataIndex: 'entryId', width: 120 },
                  // Only worth a column when rows can come from more than one account.
                  ...(ledgerData.hierarchy?.includesChildren ? [{
                    title: 'Account', dataIndex: 'accountCode', width: 190,
                    render: (code, r) => (
                      <Tooltip title={r.accountName}>
                        <Tag color={r.isOwnPosting ? 'default' : 'blue'}>{code} {r.accountName}</Tag>
                      </Tooltip>
                    ),
                    filters: (ledgerData.hierarchy.children || []).map(c => ({ text: `${c.code} ${c.name}`, value: c.code })),
                    onFilter: (val, rec) => rec.accountCode === val,
                  }] : []),
                  { title: 'Narration', dataIndex: 'narration', ellipsis: true },
                  { title: 'Source', dataIndex: 'source', width: 140, render: (s) => <Tag>{s}</Tag> },
                  { title: 'Debit', dataIndex: 'debit', render: (v) => v > 0 ? `₹${fmt(v)}` : '', align: 'right', width: 130 },
                  { title: 'Credit', dataIndex: 'credit', render: (v) => v > 0 ? `₹${fmt(v)}` : '', align: 'right', width: 130 },
                  { title: 'Balance', dataIndex: 'runningBalance', render: (v) => `₹${fmt(v)}`, align: 'right', width: 140,
                    onCell: () => ({ style: { fontWeight: 'bold' } }),
                  },
                ]}
                pagination={{ pageSize: 50 }}
                size="small"
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
